import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader.ts';
import { preloadBylineAppContentProperties } from '@confluence/content-header/entry-points/preloadBylineAppContentProperties.ts';
// @ts-ignore-next-line Ignore error that preloadContentTypesHeader has no types, it's a JS file
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadEditorContentUnifiedQuery } from '@confluence/editor-content-unified-query/entry-points/preloadEditorContentUnifiedQuery.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { preloadOutgoingLinks } from '@confluence/connectors/entry-points/preloadOutgoingLinks.ts';
import { fg } from '@confluence/feature-gating';
import { preloadFocusedInlineComment } from '@confluence/inline-comments/entry-points/preloadFocusedInlineComment.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery.ts';
import { preloadLandUsersInEditorWithRevampedBpqa } from '@confluence/onboarding-helpers/entry-points/preloadLandUsersInEditorWithRevampedBpqa.ts';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge.ts';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
	SYSTEM_CONTENT_BYLINE,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadLivePageData } from '@confluence/load-live-page/entry-points/preloadLivePageData.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { getPageExtensionListPreloadOptions } from '@confluence/forge-ui/entry-points/getPageExtensionListPreloadOptions.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadObjectComments } from '@confluence/object-comments/entry-points/preloadObjectComments.ts';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck.ts';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import { preloadSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/preloadSpaceEnabledContentTypes.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';
import { preloadIncomingLinksSsrState } from '@confluence/byline-connections/entry-points/preloadIncomingLinksSsrState.ts';
import { preloadOutboundBylineSsrState } from '@confluence/byline-connections/entry-points/preloadOutboundBylineSsrState.ts';
import { preloadRelatedContentSsrState } from '@confluence/byline-connections/entry-points/preloadRelatedContentSsrState.ts';
import { shouldPreloadBylineConnectionsSsrState } from '@confluence/byline-connections/entry-points/shouldPreloadBylineConnectionsSsrState.ts';

import { preloadConnectedThirdPartyConfig } from './preloadConnectedThirdPartyConfig.ts';

/*
 * preloadLivePageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadLivePageRoute = async (
	match: RouteMatch,
	url: string,
): Promise<void | any[] | Error[]> => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		activationId,
		accessStatus,
		isLoggedIn,
		isAdminHubAIEnabled,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isLitePage = Boolean(match.query.lite);
	const isBlog = Boolean(matchBlogPage(url));
	const shouldPreloadNavigation = !isEmbeddedConfluence;
	// Called inside the content-main data fetcher on streaming SSR so its query boundary owns it.
	const createEditorContentPreloadTasks = (): Promise<unknown>[] =>
		expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
			? [
					prepareGuardExceptionTask('EditorContentUnifiedQuery', () =>
						preloadEditorContentUnifiedQuery({ contentId, isLivePage: true }),
					),
				]
			: [];

	// Warm byline SSR state (mirrors preloadViewPageRoute) to avoid the live-page post-paint flash.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isReactSsr = Boolean(process.env.REACT_SSR);
	const shouldPreloadBylineConnections = shouldPreloadBylineConnectionsSsrState({
		isReactSsr,
		isLoggedIn,
		cloudId,
		contentId,
	});
	// Invoked exactly once per request (see streaming/non-streaming scheduling below).
	const createBylinePreloadTasks = (): Promise<unknown>[] =>
		shouldPreloadBylineConnections
			? [
					preloadIncomingLinksSsrState({
						cloudId,
						contentId,
						contentType: 'page',
						route: 'live',
					}),
					preloadOutboundBylineSsrState(contentId, 'live'),
					preloadRelatedContentSsrState({
						cloudId,
						activationId,
						contentId,
						contentType: 'page',
						route: 'live',
					}),
				]
			: [];

	if (isReactSsr && isSSRStreaming()) {
		tasks.push(
			createDataFetcherForSuspense('byline-apps', () => [
				preloadWebItemLocation({ contentId, location: SYSTEM_CONTENT_BYLINE }),
				preloadUseExtensionList({ contentId, spaceKey, moduleType: FORGE_MODULE_BYLINE }).then(
					// Property keys depend only on Forge discovery, not the editor's content-main preload.
					() => preloadBylineAppContentProperties(contentId),
				),
			]),
		);
	}

	if (expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
		const livePagePreloadOptions = {
			userId,
			contentId,
			spaceKey,
			cloudId,
			isEmbeddedConfluence,
			isSsrRoutePreload: isReactSsr,
		};
		const preloadPromise = preloadLivePageData(livePagePreloadOptions);

		tasks.push(
			isSSRStreaming()
				? createDataFetcherForSuspense('content-main', () => [
						preloadPromise,
						...createEditorContentPreloadTasks(),
						...createBylinePreloadTasks(),
					])
				: preloadPromise,
		);
	} else {
		const editorPreloadOptions = {
			spaceKey,
			contentId,
			shouldPreloadMentionsProvider: true,
			useCache: false,
			cloudId,
			isBlog,
			isLivePage: true,
			isSsrRoutePreload: isReactSsr,
		};
		const preloadPromise = prepareGuardExceptionTask('EditorData', () =>
			preloadEditorData(editorPreloadOptions),
		);

		tasks.push(
			isSSRStreaming()
				? createDataFetcherForSuspense('content-main', () => [
						preloadPromise,
						...createEditorContentPreloadTasks(),
						...createBylinePreloadTasks(),
					])
				: preloadPromise,
		);
	}

	// Non-streaming SSR awaits every task before render, so top-level byline tasks suffice.
	if (!isSSRStreaming()) {
		tasks.push(...createEditorContentPreloadTasks(), ...createBylinePreloadTasks());
	}

	// Warm the third-party connector-config *code chunk* off the editor's critical
	// path so `useConnectedThirdPartyProductKeys` has its module ready at editor
	// mount. This intentionally does NOT issue the connector-config network request
	// — doing so here plants a stale-while-revalidate cache entry that the
	// editor-mount hook then serves stale from, wedging the quick-insert menu on a
	// partial app list. See preloadConnectedThirdPartyConfig for the full rationale.
	// Client-only. Fire-and-forget: intentionally not pushed into `tasks`.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		preloadConnectedThirdPartyConfig();
	}

	// 1. Byline and possibly content prerequisites
	tasks.push(
		preloadContentHeader({
			spaceKey,
			contentId,
			isBlog,
			username,
			edition,
			isPrevEditRoute: false,
			useNewContentTopper: true,
			isLivePageRoute: true,
			isEmbeddedConfluence,
			isContentTypePublicLinkEligible: true,
		}).then((resultOrHasErrorBoolean: boolean | { results: unknown; hasErrors: boolean }) => {
			if (typeof resultOrHasErrorBoolean === 'boolean') {
				if (resultOrHasErrorBoolean) {
					return preloadContentPrerequisites(contentId, spaceKey);
				}
			} else {
				const { results, hasErrors } = resultOrHasErrorBoolean;
				if (!hasErrors) {
					return results;
				}
				return preloadContentPrerequisites(contentId, spaceKey);
			}
		}),
	);
	if (
		isEmbeddedConfluence ||
		isLitePage ||
		!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
	) {
		tasks.push(preloadLegacyBridge({ contentId, isBlog }));
	}
	if (
		isEmbeddedConfluence ||
		isLitePage ||
		!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
	) {
		if (!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
			tasks.push(preloadPageStatusQuery(contentId));
		}
	}

	// 2. Page title
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		tasks.push(preloadPageTitleContentProperties({ contentId, spaceKey }));

	// 2a. Space enabled content types
	if (spaceKey) {
		tasks.push(preloadSpaceEnabledContentTypes(spaceKey));
	}

	// 3. Space details
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true) &&
		tasks.push(preloadSpaceDetail(spaceKey));

	if (isSSRStreaming()) {
		tasks.push(
			createDataFetcherForSuspense('content-types-header', () => {
				const contentTypesHeaderPreloadTasks: Promise<any>[] = [];

				// 4. Content types header (object header)
				if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
					contentTypesHeaderPreloadTasks.push(preloadContentTypesHeader(contentId));
				}
				// (No need to call preloadPageRestrictionsQuery() here; it will be called by
				//  preloadContentHeader() above)

				// 4a. Unified Share and Restrict Button
				if (
					StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') ||
					!expVal('confluence_exp_vp_document', 'isEnabled', false)
				) {
					if (
						!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
						!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
					) {
						contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
					}
				}

				return contentTypesHeaderPreloadTasks;
			}),
		);

		// 5. Navigation and page tree
		if (shouldPreloadNavigation) {
			tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog, true));
		}
	} else {
		if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
			tasks.push(preloadContentTypesHeader(contentId));
		}
		// (No need to call preloadPageRestrictionsQuery() here; it will be called by
		//  preloadContentHeader() above)

		if (
			StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false)
		) {
			if (
				!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
				!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
			) {
				tasks.push(preloadShareAndRestrictButtonQuery(contentId));
			}
		}

		if (shouldPreloadNavigation) {
			tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog));
		}
	}

	// 6. Inline Comments
	if (
		match?.query.focusedCommentId &&
		!(
			isRelayEnabled('@confluence/object-comments', {
				operationName: 'ObjectCommentContainerPreloadObjectCommentsQuery',
			}) &&
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_conversation_query') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false)
		)
	) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// 7. Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// TODO: delete
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		tasks.push(
			preloadPageReactions({
				pageId: contentId,
				containerId: spaceKey,
			}),
		);
	}

	// 10. Stuff only needed for initial load
	tasks.push(
		// We only refresh token when it is about to expire.
		preloadMediaToken(contentId, cloudId),
	);
	if (
		isEmbeddedConfluence ||
		!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)
	) {
		if (!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
			void preloadCustomHeaderAndFooter(spaceKey);
		}
	}

	if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
		tasks.push(
			preloadWebPanelLocations({
				contentId,
				locations: [ATL_GENERAL, ATL_FOOTER],
			}),
		);
	} else {
		tasks.push(
			preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
		);
	}

	// 11. Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		tasks.push(
			createDataFetcherForSuspense('ai-floating-context-menu', () =>
				preloadAIFloatingContextMenu(contentId, isBlog),
			),
		);
	}

	// 12. Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
			}),
		);
	}

	// The main layout needs banner discovery before any content-main boundary is rendered.
	const extensionListOptions = getPageExtensionListPreloadOptions({
		contentId,
		spaceKey,
		preloadMacros:
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('confluence_preload_forge_viewport_heights_editor') ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('conf_frontend_forge_static_macro_mvp'),
	});
	if (extensionListOptions) {
		tasks.push(preloadUseExtensionList(extensionListOptions));
	}

	tasks.push(
		createDataFetcherForSuspense('object-comments', () => [
			// 15. Preload footer comments
			preloadObjectComments(contentId, match?.query.focusedCommentId as string | undefined),
			// Preload add comment permission check for the redesigned footer comment experience
			preloadAddCommentPermissionCheck(contentId, ['draft', 'current']),
		]),
	);

	// 17. Recommendations
	// Only preload recommendations for authenticated users (knowledge_discovery service requires auth)
	if (isLoggedIn) {
		const recommendationsTimeout = StatsigConfigurations.getNumber(
			'confluence_frontend_preload_recommendation_timeout',
			SLOW_QUERY_DEFAULT_TIMEOUT,
		);
		tasks.push(
			slowQueryGuard(
				'preloadEndOfPageRecommendation',
				() => {
					return preloadEndOfPageRecommendation({
						contentId,
						spaceKey,
						contentType: isBlog ? 'blogpost' : 'page',
						cloudId,
					});
				},
				recommendationsTimeout,
			),
		);

		if (
			!isBlog &&
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			UNSAFE_expValEqualsNoExposure('confluence_3p_in_related_content', 'shouldEvaluate', true)
		) {
			tasks.push(preloadOutgoingLinks(contentId));
		}
	}

	//18. Preload Smart Create Feedback Banner
	if (!expValEquals('confluence_editor_content_unified_query', 'isEnabled', true)) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	//19. Add Onboarding State for revamped bpqa
	tasks.push(preloadLandUsersInEditorWithRevampedBpqa());

	return waitForAllReturnWhenError(tasks);
};
